import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MessageCircle, Phone, Home, Globe2, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

const RANGES = [
  { key: "today", label: "Today", short: "Today" },
  { key: "yesterday", label: "Yesterday", short: "Yesterday" },
  { key: "last_week", label: "Last 7 days", short: "7D" },
  { key: "last_month", label: "Last 30 days", short: "30D" },
  { key: "all", label: "All time", short: "All" },
];

const TYPE_META = {
  whatsapp: { label: "WhatsApp clicks", icon: MessageCircle, accent: "#25D366" },
  call: { label: "Call clicks", icon: Phone, accent: "#B08543" },
  roni_install_cta: { label: "Add-Roni-to-Home clicks", icon: Home, accent: "#B08543" },
};

const COUNTRY_ISO = {
  "United Arab Emirates": "ae", "United Kingdom": "gb", "United States": "us", "Saudi Arabia": "sa",
  "India": "in", "Pakistan": "pk", "Canada": "ca", "Australia": "au", "France": "fr", "Germany": "de",
  "Russia": "ru", "Egypt": "eg", "Jordan": "jo", "Lebanon": "lb", "Kuwait": "kw", "Qatar": "qa",
  "Oman": "om", "Bahrain": "bh", "Turkey": "tr", "China": "cn", "Nigeria": "ng", "South Africa": "za",
  "Netherlands": "nl", "Spain": "es", "Italy": "it", "Singapore": "sg",
};

/**
 * CRM drill-down page: click on a stat card (WhatsApp / Call / Roni install)
 * on /admin/stats and land here with a filterable, sortable log of every
 * click of that type — timestamp, country (with flag), and originating path.
 * Powered by GET /api/stats/clicks?type=&range=&limit=.
 */
export default function AdminClickLog() {
  const { type } = useParams();
  const meta = TYPE_META[type] || { label: type, icon: Globe2, accent: "#B08543" };
  const [range, setRange] = useState("last_month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (r = range) => {
    setLoading(true);
    try {
      const res = await api.get("/stats/clicks", { params: { type, range: r, limit: 500 } });
      setData(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, range]);

  const Icon = meta.icon;
  const items = data?.items || [];

  return (
    <div data-testid={`admin-click-log-${type}`}>
      <Link
        to="/admin/stats"
        data-testid="click-log-back"
        className="inline-flex items-center gap-2 mb-6 text-[11px] font-mono-brand tracking-[0.14em] uppercase text-propertic-muted hover:text-propertic-navy transition-colors"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Stats
      </Link>

      <div className="mb-6 flex items-start gap-4">
        <div
          className="w-11 h-11 rounded-2xl grid place-items-center border hairline flex-shrink-0"
          style={{ backgroundColor: `${meta.accent}15`, borderColor: `${meta.accent}40` }}
        >
          <Icon className="w-5 h-5" style={{ color: meta.accent }} />
        </div>
        <div>
          <p className="font-mono-brand text-propertic-gold mb-2 text-[10px] sm:text-xs tracking-[0.14em] uppercase">Click log</p>
          <h1 className="font-editorial text-3xl sm:text-4xl text-propertic-navy">{meta.label}</h1>
          <p className="text-propertic-muted text-xs sm:text-sm mt-2 max-w-2xl">
            Every logged click, newest first. Country is resolved from the visitor's IP at the moment
            of the click; IPs themselves are never stored.
          </p>
        </div>
      </div>

      <div className="mb-8 flex flex-wrap items-center gap-2" data-testid="click-log-controls">
        <div className="flex flex-wrap items-center gap-1 bg-white rounded-2xl border hairline p-1 max-w-full">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              data-testid={`click-log-range-${r.key}`}
              className={`h-8 px-3 rounded-xl font-mono-brand text-[10px] tracking-[0.12em] uppercase whitespace-nowrap transition-colors ${range === r.key ? "bg-propertic-navy text-white" : "text-propertic-muted hover:text-propertic-navy"}`}
            >
              <span className="sm:hidden">{r.short}</span>
              <span className="hidden sm:inline">{r.label}</span>
            </button>
          ))}
        </div>
        <button
          onClick={() => load(range)}
          disabled={loading}
          data-testid="click-log-refresh"
          className="h-9 px-3 rounded-full border hairline text-[10px] font-mono-brand tracking-[0.12em] uppercase text-propertic-navy hover:border-propertic-gold hover:text-propertic-gold inline-flex items-center gap-2 disabled:opacity-60"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {loading && !data && <p className="text-sm text-propertic-muted">Loading…</p>}

      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-8">
            <MiniStat label="Total clicks" value={data.total || 0} testid="click-log-total" range={data.range_label} accent={meta.accent} />
            <MiniStat label="Shown here" value={data.returned || 0} testid="click-log-returned" range={`Max 500 per page`} accent={meta.accent} />
            <MiniStat label="Range" value={data.range_label || "—"} testid="click-log-range" range={`${data.range_start} → ${data.range_end}`} accent={meta.accent} isText />
          </div>

          <section className="bg-white rounded-2xl border hairline overflow-hidden" data-testid="click-log-list">
            <div className="px-5 sm:px-6 py-4 border-b hairline flex items-center justify-between gap-2">
              <h2 className="font-editorial text-base sm:text-xl text-propertic-navy">Every click · {data.range_label}</h2>
              <span className="hidden sm:inline text-[11px] text-propertic-muted font-mono-brand">time · country · page</span>
            </div>

            {items.length === 0 ? (
              <p className="p-6 text-sm text-propertic-muted">
                No {meta.label.toLowerCase()} in this range. Change the filter or click a WhatsApp / Call button on the site to seed one.
              </p>
            ) : (
              <>
                {/* Mobile card list */}
                <ul className="md:hidden divide-y hairline">
                  {items.map((r, i) => {
                    const iso = COUNTRY_ISO[r.country];
                    return (
                      <li key={i} className="px-5 py-3 flex items-center gap-3" data-testid={`click-log-row-m-${i}`}>
                        {iso ? (
                          <img src={`https://flagcdn.com/w40/${iso}.png`} alt="" width="24" height="18" className="w-6 h-auto rounded-[2px] shadow-sm flex-shrink-0" />
                        ) : (
                          <Globe2 className="w-4 h-4 text-propertic-muted flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-[12px] text-propertic-navy truncate">{r.country || "Unknown"}</p>
                          <p className="text-[10px] text-propertic-muted font-mono-brand truncate">
                            {new Date(r.created_at).toLocaleString()} · {r.path || "/"}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>

                {/* Desktop table */}
                <table className="hidden md:table w-full text-sm">
                  <thead className="text-propertic-muted bg-propertic-cream/40">
                    <tr>
                      <th className="text-start px-6 py-3 font-mono-brand text-[10px] tracking-[0.14em] uppercase w-[220px]">Date · Time</th>
                      <th className="text-start px-6 py-3 font-mono-brand text-[10px] tracking-[0.14em] uppercase w-[200px]">Country</th>
                      <th className="text-start px-6 py-3 font-mono-brand text-[10px] tracking-[0.14em] uppercase">Page</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y hairline">
                    {items.map((r, i) => {
                      const iso = COUNTRY_ISO[r.country];
                      const d = new Date(r.created_at);
                      return (
                        <tr key={i} className="hover:bg-propertic-cream/40" data-testid={`click-log-row-${i}`}>
                          <td className="px-6 py-3 text-propertic-navy font-mono-brand text-[11px] whitespace-nowrap">
                            {d.toLocaleDateString()} · {d.toLocaleTimeString()}
                          </td>
                          <td className="px-6 py-3">
                            <div className="flex items-center gap-2">
                              {iso ? (
                                <img src={`https://flagcdn.com/w40/${iso}.png`} alt="" width="20" height="15" className="w-5 h-auto rounded-[2px] shadow-sm" />
                              ) : (
                                <Globe2 className="w-4 h-4 text-propertic-muted" />
                              )}
                              <span className="text-propertic-ink">{r.country || "Unknown"}</span>
                            </div>
                          </td>
                          <td className="px-6 py-3 text-propertic-muted text-[12px] truncate max-w-[420px]">{r.path || "/"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function MiniStat({ label, value, range, testid, accent, isText }) {
  return (
    <div className="bg-white rounded-2xl border hairline p-4 sm:p-5" data-testid={testid}>
      <p className="font-mono-brand text-propertic-muted text-[9px] sm:text-[10px] tracking-[0.12em] uppercase leading-tight">{label}</p>
      <p className={`font-editorial mt-2 sm:mt-3 text-propertic-navy tabular ${isText ? "text-lg sm:text-2xl" : "text-3xl sm:text-4xl"}`} style={{ color: accent && !isText ? "#0F1E2E" : undefined }}>
        {value}
      </p>
      {range && <p className="text-[10px] sm:text-[11px] text-propertic-muted mt-1 font-mono-brand truncate">{range}</p>}
    </div>
  );
}
